import React from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
} from '@mui/material';
import { Close as CloseIcon, SmartToy as SmartToyIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { BotMessage } from '../hooks/useChatbotState';

interface HistorySidebarProps {
  showSidebar: boolean;
  setShowSidebar: (show: boolean) => void;
  conversationHistory: BotMessage[];
  getUserInitials: () => string;
}

export const HistorySidebar: React.FC<HistorySidebarProps> = React.memo(
  ({ showSidebar, setShowSidebar, conversationHistory, getUserInitials }) => {
    const theme = useTheme();

    return (
      <Drawer anchor="right" open={showSidebar} onClose={() => setShowSidebar(false)}>
        <Box sx={{ width: 300, padding: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Conversation History
            </Typography>
            <IconButton size="small" onClick={() => setShowSidebar(false)} aria-label="Close history sidebar">
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ marginBottom: 2 }} />
          <List>
            {conversationHistory.length === 0 ? (
              <Typography variant="caption" sx={{ opacity: 0.6 }}>
                No messages yet
              </Typography>
            ) : (
              conversationHistory.map(msg => (
                <ListItem
                  key={msg.id}
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    marginBottom: 1,
                    padding: 1,
                    backgroundColor: msg.role === 'user' ? `${theme.palette.primary.main}15` : 'transparent',
                    borderRadius: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {msg.role === 'user' ? (
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: 'primary.main',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: 600,
                          }}
                        >
                          {getUserInitials()}
                        </Avatar>
                      ) : (
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                          }}
                        >
                          <SmartToyIcon sx={{ fontSize: 12 }} />
                        </Avatar>
                      )}
                    </ListItemIcon>
                    <Box sx={{ flex: 1 }}>
                      <ListItemText
                        primary={msg.content.substring(0, 50) + '...'}
                        secondary={msg.timestamp.toLocaleTimeString()}
                        primaryTypographyProps={{ variant: 'caption' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </Box>
                  </Box>
                </ListItem>
              ))
            )}
          </List>
        </Box>
      </Drawer>
    );
  },
);

HistorySidebar.displayName = 'HistorySidebar';
